import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/Button";

import { DatabaseCollectionDto } from "../lib/generated/api-client";
import { Field, fieldLabel, isReadOnlyField } from "../lib/schema-field";
import { AutocompleteSelect } from "./AutocompleteSelect";
import { DynamicFormField } from "./DynamicFormField";
import { Modal } from "./Modal";

type BulkEditModalProps = {
  open: boolean;
  onCancel: () => void;
  onApply: (values: Record<string, any>) => void;
  fields: Field[];
  count: number;
  applying?: boolean;
  /** Live copy while writes are in flight, e.g. "Updating 12 of 40". */
  progress?: string;
  collection?: DatabaseCollectionDto;
  demoMode?: boolean;
};

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  open,
  onCancel,
  onApply,
  fields,
  count,
  applying = false,
  progress,
  collection,
  demoMode,
}) => {
  const writable = useMemo(
    () => fields.filter((field) => !isReadOnlyField(field)),
    [fields]
  );
  const [fieldNames, setFieldNames] = useState<string[]>([]);
  const [model, setModel] = useState<Record<string, any>>({});
  const [confirmingClear, setConfirmingClear] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFieldNames([]);
    setModel({});
    setConfirmingClear(false);
  }, [open]);

  const chosenFields = writable.filter((item) => fieldNames.includes(item.name));
  const clearingFields = chosenFields.filter((field) =>
    model[field.name] === undefined || model[field.name] === null || model[field.name] === ""
  );
  const noun = count === 1 ? "record" : "records";
  const canApply = chosenFields.length > 0 && !applying;

  const handleCancel = () => {
    if (applying) return;
    onCancel();
  };

  const handleApply = () => {
    if (!canApply) return;
    if (clearingFields.length > 0 && !confirmingClear) {
      setConfirmingClear(true);
      return;
    }
    onApply(Object.fromEntries(chosenFields.map((field) => [field.name, model[field.name]])));
  };

  return (
    <Modal
      open={open}
      title={`Edit ${count} ${noun}`}
      onClose={handleCancel}
      variant="dialog"
      maxWidth="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={confirmingClear ? () => setConfirmingClear(false) : handleCancel}
            disabled={applying}
            variant="secondary" size="sm"
          >
            {confirmingClear ? "Back" : "Cancel"}
          </Button>
          <Button
            onClick={handleApply}
            disabled={!canApply}
            variant="primary" size="sm"
          >
            {applying && progress ? progress : confirmingClear ? "Confirm and apply" : `Set on ${count} ${noun}`}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-sm text-od-text">
        <p className="m-0">
          This updates {count} {noun} in your database. It cannot be undone from here.
        </p>
        {confirmingClear ? (
          <p role="alert" className="m-0">
            Clear {clearingFields.map(fieldLabel).join(", ")} on {count} {noun}?
            {" "}All selected field changes will be applied together.
          </p>
        ) : writable.length === 0 ? (
          <p className="m-0 text-od-muted">
            This table has no writable columns.
          </p>
        ) : (
          <>
            <div>
              <AutocompleteSelect
                label="Fields"
                placeholder="Choose fields"
                multiple
                options={writable.map((field) => ({ label: fieldLabel(field), value: field.name }))}
                value={fieldNames}
                disabled={applying}
                onChange={(value) => {
                  const names = Array.isArray(value) ? value : [];
                  setFieldNames(names);
                  setModel((prev) => Object.fromEntries(names.map((name) => [name, prev[name]])));
                }}
              />
            </div>
            {chosenFields.map((field) => (
              <DynamicFormField
                key={field.name}
                field={field}
                collection={collection}
                demoMode={demoMode}
                model={model}
                onChangeForm={(name, val) =>
                  !applying && setModel((prev) => ({ ...prev, [name]: val }))
                }
              />
            ))}
            {applying && progress && (
              <p className="m-0 text-xs text-od-muted">{progress}</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkEditModal;
